import { Button, Stack } from "@mantine/core";
import { client } from "@/api-client";
import type { PublicProfileInput, SocialLinkInput } from "@/api-types";
import { FancyLoader } from "@/components/FancyLoader";
import { Header } from "@/components/Header";
import { usePublicProfileForm } from "@/components/PublicProfileForm";
import { UniversalRoutes, useNavigator } from "@/routers/routes";
import { useUser } from "@/UserContext";

export const PublicProfileWhenLoaded = ({
  data,
}: {
  data?: PublicProfileInput;
}) => {
  const user = useUser();
  const { navigate } = useNavigator();
  const backendValuesToFormValues = (
    backendProfileData?: PublicProfileInput,
  ) => {
    if (!backendProfileData) {
      return {
        headline: "",
        description: "",
        specialities: [] as string[],
        languages: [] as string[],
        testimonials: [] as string[],
        education: [] as string[],
        links: [] as SocialLinkInput[],
      };
    }
    return backendProfileData;
  };

  const formValues = backendValuesToFormValues(data);

  const updatePublicProfile = client.useMutation(
    "post",
    "/trainer/profile/public",
  );

  const handleSubmit = async (
    values: PublicProfileInput,
    shouldPreview: boolean = false,
  ) => {
    await updatePublicProfile.mutateAsync({
      body: values,
    });
    if (shouldPreview) {
      navigate({
        path: UniversalRoutes.TRAINERS.DETAIL,
        params: { trainerId: user.accountId },
      });
    } else {
      navigate({ path: UniversalRoutes.PROFILE.HOME });
    }
  };

  const publicProfileForm = usePublicProfileForm({
    initialValues: formValues,
    mode: "full",
    onFormSubmit: (values, isPreview) => {
      handleSubmit(values, isPreview);
    },
  });

  return (
    <Stack gap="xs">
      <Header title="Public Profile" />
      {publicProfileForm.form}

      <Button
        onClick={() => publicProfileForm.submit(false)}
        fullWidth
        loading={updatePublicProfile.isPending}
      >
        Save
      </Button>
      <Button
        onClick={() => publicProfileForm.submit(true)}
        fullWidth
        variant="outline"
        loading={updatePublicProfile.isPending}
      >
        Save and preview
      </Button>
    </Stack>
  );
};

export const PublicProfile = () => {
  const user = useUser();
  const { data } = client.useQuery(
    "get",
    "/trainer/profile/public",
    {
      enabled: user.isProfileCreated,
    },
    {
      staleTime: 0,
      gcTime: 0,
    },
  );

  if (!data) {
    return <FancyLoader />;
  }

  return <PublicProfileWhenLoaded data={data} />;
};
